import React, { useState, useEffect } from 'react';
import { Form, Button, ListGroup } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useCreateDepositMutation, useUploadDepositImagesMutation } from '../../slices/depositsApiSlice';
import { useGetCustomersQuery } from '../../slices/customersApiSlice';
import { useSelector } from 'react-redux';

const AddDepositScreen = () => {
  const navigate = useNavigate();
  const { userInfo } = useSelector((state) => state.auth);

  // Form State
  const [formData, setFormData] = useState({
    date: '',
    account: '',
    accountName: '',
    subAccount: '',
    details: '',
    amount: '',
    category: '',
    notes: '',
    images: [],
  });

  // Customer Search State
  const [customerSearch, setCustomerSearch] = useState('');
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  // Queries & Mutations
  const { data: customersData } = useGetCustomersQuery({ keyword: customerSearch });
  const [createDeposit, { isLoading, error }] = useCreateDepositMutation();
  const [uploadDepositImages] = useUploadDepositImagesMutation();

  // Fetch and filter customers dynamically
  useEffect(() => {
    if (customerSearch.length >= 3 && customersData?.customers) {
      setFilteredCustomers(
        customersData.customers.filter((customer) =>
          customer.name.toLowerCase().includes(customerSearch.toLowerCase())
        )
      );
    } else {
      setFilteredCustomers([]);
    }
  }, [customerSearch, customersData]);

  const handleCustomerSelect = (customer) => {
    setSelectedCustomer(customer);
    setFormData((prevData) => ({
      ...prevData,
      accountName: customer.name,
    }));
    setFilteredCustomers([]);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));

    if (name === 'accountName') {
      setCustomerSearch(value);
    }
  };

  const submitHandler = async (e) => {
    e.preventDefault();
    try {
      await createDeposit({
        ...formData,
        accountName: selectedCustomer?.name || formData.accountName,
        username: userInfo.name || '',
      }).unwrap();
      toast.success('Deposit added successfully');
      navigate('/admin/depositlist');
    } catch (err) {
      toast.error(err?.data?.message || 'Failed to add deposit');
    }
  };

  const validateFiles = (files) => {
    const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg'];
    const maxSize = 5 * 1024 * 1024; // 5MB
    for (let file of files) {
      if (!allowedTypes.includes(file.type)) {
        toast.error(`File type not allowed: ${file.type}`);
        return false;
      }
      if (file.size > maxSize) {
        toast.error(`File size exceeds 5MB: ${file.name}`);
        return false;
      }
    }
    return true;
  };

  const uploadImageHandler = async (files) => {
    if (!validateFiles(files)) return;

    const formDataUpload = new FormData();
    Array.from(files).forEach((file) => formDataUpload.append('images', file));

    try {
      const res = await uploadDepositImages(formDataUpload).unwrap();
      toast.success(res.message);
      setFormData((prevData) => ({
        ...prevData,
        images: [...prevData.images, ...res.images],
      }));
    } catch (err) {
      toast.error(err?.data?.message || err.error);
    }
  };

  const removeImageHandler = (index) => {
    setFormData((prevData) => ({
      ...prevData,
      images: prevData.images.filter((_, i) => i !== index),
    }));
  };

  return (
    <div className="container mt-4">
      <h1>Add New Deposit</h1>
      <Link to="/admin/depositlist" className="btn btn-light my-3">
        Go Back
      </Link>
      <Form onSubmit={submitHandler}>
        <Form.Group controlId="date" className="mb-3">
          <Form.Label>Date</Form.Label>
          <Form.Control type="date" name="date" value={formData.date} onChange={handleChange} required />
        </Form.Group>

        <Form.Group controlId="account" className="mb-3">
          <Form.Label>Account Type</Form.Label>
          <Form.Control as="select" name="account" value={formData.account} onChange={handleChange} required>
            <option value="Credit">Credit</option>
            <option value="Debit">Debit</option>
          </Form.Control>
        </Form.Group>

        <Form.Group controlId="accountName" className="mb-3">
          <Form.Label>Account Name</Form.Label>
          <Form.Control
            type="text"
            name="accountName"
            value={formData.accountName}
            placeholder="Search account name"
            onChange={handleChange}
            required
          />
          {customerSearch.length >= 3 && filteredCustomers.length > 0 && (
            <ListGroup className="dropdown-menu mt-2">
              {filteredCustomers.map((customer) => (
                <ListGroup.Item key={customer._id} action onClick={() => handleCustomerSelect(customer)}>
                  {customer.name} ({customer.company || 'No company'})
                </ListGroup.Item>
              ))}
            </ListGroup>
          )}
        </Form.Group>
        <Form.Group controlId="subAccount" className="mb-3">
                    <Form.Label>Sub-Account</Form.Label>
                    <Form.Control
                      as="select"
                      name="subAccount"
                      value={formData.subAccount}
                      onChange={handleChange}
                      required
                    >
                      <option value="LOAN">LOAN</option>
                      <option value="EXPENSE">EXPENSE</option>
                      <option value="SERVICES">SERVICES</option>
                      <option value="OTHER INCOME">OTHER INCOME</option>
                      <option value="PAYMENT">PAYMENT</option>
                      <option value="DISCOUNT">DISCOUNT</option>
                    </Form.Control>
                  </Form.Group>
          
                  <Form.Group controlId="details" className="mb-3">
                    <Form.Label>Details</Form.Label>
                    <Form.Control
                      type="text"
                      name="details"
                      value={formData.details}
                      placeholder="Enter details"
                      onChange={handleChange}
                      required
                    />
        </Form.Group>

        <Form.Group controlId="amount" className="mb-3">
          <Form.Label>Amount</Form.Label>
          <Form.Control type="number" name="amount" value={formData.amount} onChange={handleChange} required />
        </Form.Group>
        
        <Form.Group controlId="category" className="mb-3">
                    <Form.Label>Category</Form.Label>
                    <Form.Control
                      as="select"
                      name="category"
                      value={formData.category}
                      onChange={handleChange}
                    >
                      <option value="Deposit">Deposit</option>
                     <option value="Withdrawal">Withdrawal</option>
                     <option value="Other">Other</option>
                     <option value="Opening">Opening</option>
                                            
                    </Form.Control>
                  </Form.Group>
          
                  <Form.Group controlId="notes" className="mb-3">
                    <Form.Label>Notes</Form.Label>
                    <Form.Control
                      as="textarea"
                      name="notes"
                      value={formData.notes}
                      placeholder="Enter additional notes"
                      rows={3}
                      onChange={handleChange}
                    />
                  </Form.Group>
                  <Form.Group controlId="username" className="mb-3">
                    <Form.Label>User</Form.Label>
                    <Form.Control
                      as="textarea"
                      name="notes"
                      value={userInfo.name}
                      
                      rows={3}
                      onChange={handleChange}
                    />
        </Form.Group>
        <Form.Group controlId="images" className="mb-3">
          <Form.Label>Images</Form.Label>
          <Form.Control type="file" multiple onChange={(e) => uploadImageHandler(e.target.files)} />
          <div className="mt-3">
            {formData.images.map((img, idx) => (
              <div key={idx} style={{ display: 'inline-block', position: 'relative', marginRight: '10px' }}>
                <img
                  src={img}
                  alt={`Uploaded ${idx}`}
                  className="img-fluid"
                  style={{ maxWidth: '100px', height: '100px', objectFit: 'cover' }}
                />
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => removeImageHandler(idx)}
                  style={{
                    position: 'absolute',
                    top: '0',
                    right: '0',
                    borderRadius: '50%',
                    padding: '2px 5px',
                  }}
                >
                  ✖
                </Button>
              </div>
            ))}
          </div>
        </Form.Group>

        <Button type="submit" variant="primary" disabled={isLoading}>
          {isLoading ? 'Adding Deposit...' : 'Add Deposit'}
        </Button>

        {error && <p className="text-danger">{error?.data?.message || 'Error adding deposit'}</p>}
      </Form>
    </div>
  );
};

export default AddDepositScreen;